import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { money } from "@/lib/format";
import { SECTION_TONE, TONE } from "./emailTokens";

/** One chase-list section: heading strip plus the leads inside it. */
const DigestSection = ({ section }) => (
  <div
    data-testid={`digest-preview-${section.key}`}
    className="overflow-hidden rounded-lg border border-border"
  >
    <div
      className={`border-b px-3.5 py-2 ${
        SECTION_TONE[section.key] || SECTION_TONE.never_contacted
      }`}
    >
      <p className="text-[11px] font-bold uppercase tracking-[0.08em]">
        {section.title} · {section.count}
      </p>
      <p className="mt-0.5 text-[11px] leading-snug opacity-90">{section.blurb}</p>
    </div>
    <ul className="divide-y divide-border">
      {section.leads.map((lead) => (
        <li key={lead.quote_ref} className="px-3.5 py-2.5">
          <p className="text-[13px] font-bold text-foreground">
            {lead.full_name}
            <span className="font-normal text-muted-foreground">
              {" · "}
              {lead.company}
            </span>
          </p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            {lead.service_label} ·{" "}
            <span className="font-amount font-bold text-[color:var(--tt-teal-deep)]">
              {money(lead.quote_total)}
              {lead.pricing_type === "monthly" ? "/month" : ""}
            </span>{" "}
            · {lead.status_label}
          </p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            {lead.email} · {lead.phone_display}
          </p>
        </li>
      ))}
      {section.more ? (
        <li className="px-3.5 py-2 text-[11px] text-muted-foreground">
          and {section.more} more on the board
        </li>
      ) : null}
    </ul>
  </div>
);

/** People who asked for a call back and have not been ticked off yet. */
const WaitingSection = ({ digest }) => (
  <div
    data-testid="digest-preview-waiting"
    className="overflow-hidden rounded-lg border border-border"
  >
    <div className={`border-b px-3.5 py-2 ${TONE.warn}`}>
      <p className="text-[11px] font-bold uppercase tracking-[0.08em]">
        Waiting on a call back · {digest.waiting_count}
      </p>
      <p className="mt-0.5 text-[11px] leading-snug opacity-90">
        Nobody has ticked these off yet.
      </p>
    </div>
    <ul className="divide-y divide-border">
      {digest.waiting.map((person) => (
        <li key={person.reference} className="px-3.5 py-2.5">
          <p className="text-[13px] font-bold text-foreground">
            {person.full_name}
            <span className="font-normal text-muted-foreground">
              {" · "}
              {person.company}
            </span>
          </p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            {person.route_label} ·{" "}
            <span className="font-bold text-[color:var(--tt-warn-text)]">
              waiting {person.waited_label}
            </span>
          </p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            {person.email} · {person.phone_display} ·{" "}
            <span className="font-amount">{person.reference}</span>
          </p>
        </li>
      ))}
      {digest.waiting_more ? (
        <li className="px-3.5 py-2 text-[11px] text-muted-foreground">
          and {digest.waiting_more} more in the inbox
        </li>
      ) : null}
    </ul>
  </div>
);

export const DigestPreviewDialog = ({ preview, onClose }) => {
  const digest = preview?.digest;
  return (
    <Dialog open={Boolean(preview)} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        data-testid="digest-preview-dialog"
        className="max-h-[85vh] overflow-y-auto bg-white sm:max-w-xl"
      >
        <DialogHeader className="text-left">
          <DialogTitle>{digest?.subject}</DialogTitle>
          <DialogDescription>
            {digest?.for_date_label}
            {digest?.has_work
              ? ` · ${digest.total} to chase · ${money(digest.value)} in play`
              : ""}
          </DialogDescription>
        </DialogHeader>

        {!digest?.has_work ? (
          <p
            data-testid="digest-preview-empty"
            className="rounded-lg border border-border bg-[color:var(--tt-surface-2)] px-4 py-6 text-center text-sm text-muted-foreground"
          >
            Nothing needs chasing today — no overdue actions, nothing gone quiet and no untouched
            new leads. On a day like this the email stays quiet.
          </p>
        ) : (
          <div className="space-y-3">
            {digest.sections.map((section) => (
              <DigestSection key={section.key} section={section} />
            ))}
            {digest.waiting_count ? <WaitingSection digest={digest} /> : null}
            {digest.quotes_yesterday ? (
              <p className="text-[12px] text-muted-foreground">
                {digest.quotes_yesterday} new quote
                {digest.quotes_yesterday === 1 ? "" : "s"} came in yesterday.
              </p>
            ) : null}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
